import { FC, FormEventHandler, memo, MouseEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import {
  FormControl,
  FormControlProps,
  FormHelperText,
  Input as StandardInput,
  FilledInput,
  OutlinedInput,
  InputLabel,
  SxProps,
  Theme,
  IconButton,
} from "@mui/material";
import { Help } from "components/Help";
import { ReactComponent as IconPlus } from "assets/icons/plus.svg";
import { ReactComponent as IconMinus } from "assets/icons/minus.svg";
import styles from "./styles";

const Adornment = memo(({ name, fill, onClick }: { name: string; fill: string; onClick: MouseEventHandler<HTMLButtonElement> }) => {
  const Icon = name === "minus" ? IconMinus : IconPlus;
  return (
    <IconButton name={name} size="small" onClick={onClick} className="RlxNumberField-adornment">
      <Icon fontSize="inherit" fill={fill} style={{ pointerEvents: "none" }} className="RlxNumberField-icon" />
    </IconButton>
  );
});

const isValid = (value: number | undefined, min: number | undefined, max: number | undefined): boolean =>
  typeof value === "number" && Number.isFinite(value) && (min === undefined || value >= min) && (max === undefined || value <= max);

const updateValue = (value: number | undefined, step: number, { min, max }: { min?: number; max?: number } = {}): number => {
  const current = Number(value);
  if (!Number.isFinite(current)) return 0;

  let next = current + step;
  if (min !== undefined) next = Math.max(next, min);
  if (max !== undefined) next = Math.min(next, max);

  return Number.parseFloat(next.toFixed(2));
};

const updateInput = (value: string, precision: number) => {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return value;

  if (value.endsWith(".")) return precision === 0 ? Number.parseInt(value, 10) : parsed.toFixed(precision);

  const fraction = value.split(".")[1];
  if (Number.isInteger(precision) && fraction && fraction.length >= precision) return Number.parseFloat(parsed.toFixed(precision));

  return Number.parseFloat(value);
};

type Props = Omit<FormControlProps, "onChange"> & {
  name: string;
  value?: number;
  label?: string;
  tooltip?: string;
  min?: number;
  max?: number;
  step: number;
  precision?: number;
  helperText?: string;
  fill?: string;
  background?: string;
  sx?: SxProps<Theme>;
  onChange?: (name: string, value: number) => void;
};

const NumberField: FC<Props> = ({
  name,
  label,
  variant = "outlined",
  tooltip,
  min = 0,
  max = 100,
  step = 1,
  precision = 0,
  helperText,
  fill = "var(--color-primary)",
  background,
  sx = {},
  onChange,
  ...rest
}) => {
  const [value, setValue] = useState<number | string | undefined>(rest.value ?? 0);
  const Input = variant === "outlined" ? OutlinedInput : variant === "filled" ? FilledInput : StandardInput;
  const error = !isValid(value as number, min, max);

  useEffect(() => {
    setValue(rest?.value);
  }, [rest.value]);

  const commitInput = useCallback((inputValue: string) => {
    const normalized = inputValue.replace(",", ".");
    if (normalized.endsWith(".")) {
      setValue(inputValue);
      return;
    }

    const newValue = updateInput(normalized, precision);
    setValue(newValue);
    onChange?.(name, newValue as number);
  }, [name, onChange, precision]);

  const debouncedInput = useMemo(() => debounce(commitInput, 250), [commitInput]);

  useEffect(() => () => debouncedInput.cancel(), [debouncedInput]);

  const handleInput: FormEventHandler<HTMLDivElement> = useCallback((e) => {
    const input = e.target as HTMLInputElement;
    setValue(input.value);
    debouncedInput(input.value);
  }, [debouncedInput]);

  const moveStep = useCallback((delta: number) => {
    const newValue = updateValue(value as number, delta, { min, max });
    setValue(newValue);
    onChange?.(name, newValue);
  }, [max, min, name, onChange, value]);

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    moveStep(e.currentTarget.name === "minus" ? -step : step);
  }, [moveStep, step]);

  return (
    <FormControl variant={variant} error={error} sx={sx} {...rest} className="RlxNumberField">
      {label && (
        <InputLabel htmlFor={`number-field-${name}`} sx={styles.label} className="RlxNumberField-label">
          {label}
          {tooltip && <Help text={tooltip} />}
        </InputLabel>
      )}
      <Input
        id={`number-field-${name}`}
        name={name}
        value={value}
        inputProps={{ inputMode: "numeric", min, max, step }}
        onInput={handleInput}
        sx={styles.input}
        startAdornment={<Adornment name="minus" fill={fill} onClick={handleClick} />}
        endAdornment={<Adornment name="plus" fill={fill} onClick={handleClick} />}
        className="RlxNumberField-input"
      />
      {error && (
        <FormHelperText sx={styles.error} className="RlxNumberField-formHelperText">
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

NumberField.displayName = "NumberField";

export default memo(NumberField);
